import React, { memo, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Avatar from '@material-ui/core/Avatar'
import { useMutation, useLazyQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'

import { DeleteCompanyUserRole, StoreValue, UserQuery } from 'store'
import styles from './styles'
import { MoreBtnMenu } from '.'
import { buildUserAvatar, EnumUserRoles } from 'utils'
import { IUserAccountRow, EAppStateActionTypes } from 'models'
import classNames from 'classnames'

const useStyles = makeStyles(styles)

const UserAccountsRowView: React.FunctionComponent<IUserAccountRow> = ({
	item,
	companyRoleId,
	roleType,
	companyId,
	companyRolesIds,
	storeUserId,
	storeDispatch,
}) => {
	const [t] = useTranslation()
	const classes = useStyles()
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	const [getLoggedUser, { data: loggedUserData }] = useLazyQuery(UserQuery, {
		fetchPolicy: 'network-only',
	})

	const [deleteCompanyUserRoleMutation] = useMutation(DeleteCompanyUserRole)

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorEl(event.currentTarget)
		},
		[setAnchorEl],
	)

	const onClickStopPropagation = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation()
	}

	const handleClose = useCallback(() => {
		setAnchorEl(null)
	}, [setAnchorEl])

	const handleActionClick = useCallback(
		(userId: string) => {
			handleClose()
			deleteCompanyUserRoleMutation({
				variables: {
					data: {
						userId,
						id: companyRoleId,
					},
				},
			}).then(() => {
				if (userId === storeUserId)
					getLoggedUser({
						variables: {
							id: userId,
						},
					})
			})
		},
		[
			deleteCompanyUserRoleMutation,
			companyRoleId,
			getLoggedUser,
			storeUserId,
			handleClose,
		],
	)

	let userRoles: string = ''
	if (item[EnumUserRoles.COMPANY_ADMIN]) {
		userRoles = `${t('Administrator')}, ${t('Manager')}, ${t(
			'Job publiser',
		)}, ${t('Moderator')}, ${t('Publisher')}`
	} else if (item[EnumUserRoles.COMPANY_MANAGER]) {
		userRoles = `${t('Manager')}, ${t('Job publiser')}, ${t(
			'Moderator',
		)}, ${t('Publisher')}`
	} else if (item[EnumUserRoles.JOB_PUBLISHER]) {
		userRoles = `${t('Job publiser')}, ${t('Moderator')}, ${t('Publisher')}`
	} else if (item[EnumUserRoles.MODERATOR]) {
		userRoles = `${t('Moderator')}, ${t('Publisher')}`
	} else if (item[EnumUserRoles.PUBLISHER]) {
		userRoles = `${t('Publisher')}`
	}

	useEffect(() => {
		if (loggedUserData) {
			storeDispatch({
				type: EAppStateActionTypes.updateUser,
				user: loggedUserData.user,
			})
		}
	}, [loggedUserData, storeDispatch])

	return (
		<Grid
			item
			className={classes.cardItemWrap}
			onClick={event => onClickStopPropagation(event)}
		>
			<div
				className={classNames(classes.cardItem, {
					[classes.cardItemDisabled]: item.inherited,
				})}
			>
				<div>
					<div className={classes.avatarContent}>
						<Avatar
							classes={{ root: classes.profilePicture }}
							src={
								item &&
								buildUserAvatar(
									item.id,
									item.profile.avatarPath,
								)
							}
						></Avatar>

						<div className={classes.details}>
							<span className={classes.name}>
								{`${item.profile.firstName} ${item.profile.lastName}`}
							</span>
						</div>
					</div>
					{item.inherited && (
						<div className={classes.rolesDescription}>{`${t(
							'Roles',
						)}: ${userRoles}`}</div>
					)}
				</div>
				{!item.inherited ? (
					<div className={classes.menuIcon}>
						<MoreBtnMenu
							userId={item.id}
							anchorEl={anchorEl}
							handleActionClick={handleActionClick}
							handleClose={handleClose}
							handleMenuClick={handleClick}
							roleType={roleType}
							companyId={companyId}
							companyRoleId={companyRoleId}
							companyRolesIds={companyRolesIds}
						/>
					</div>
				) : (
					<div className={classes.cardItemInfo}>
						<div>{t('Implicitly')}</div>
					</div>
				)}
			</div>
		</Grid>
	)
}

const UserAccountsRow = memo((props: any) => (
	<StoreValue keys={['user.id|storeUserId', 'storeDispatch']}>
		<UserAccountsRowView {...props} />
	</StoreValue>
))

export default memo(UserAccountsRow)
